import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { EQuestionType } from '@hubertai/screening-question-types';
import { toast } from 'sonner';
import { useQuestionEditorContext } from '../context/create-question.context';
import TextBubbleDragAndDrop from 'components/TextBubbleDragAndDrop';
import type { TSortableInput } from 'components/TextBubbleDragAndDrop';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';

import {
  selectQuestionIsReadonly,
  selectQuestionLanguage,
  selectWritesTemplateOverrides,
} from 'rtk/questionEditorSelector';
import { customParametersUpdate } from 'rtk/TemplateSlice';
import { EEventType } from 'types/events.enum';
import { useTemplateId } from 'hooks/useTemplate';
import TextBubblev2 from 'components/TextBubbleDragAndDrop/TextBubblev2';
import TextBubbleDragAndDropHeaderv2 from 'components/TextBubbleDragAndDrop/TextBubbleDragAndDropHeadev2';
import { dragAndDropText } from 'rtk/Question.Editor.Slice';

const CreateTextBubble = () => {
  const { t } = useTranslation('Questionsv2');
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const isReadOnly = useAppSelector((state) => selectQuestionIsReadonly(state, questionId));
  const { intro, outro, clarify, text, type } = useAppSelector((state) => state.questionEditor.byId[questionId]);
  const templateId = useTemplateId();
  const writesTemplateOverrides = useAppSelector((state) => selectWritesTemplateOverrides(state, questionId));
  if (!type || !type.length || !questionLanguage || !questionLanguage.length) {
    return <div className="text-danger">{t('createTextBubble.pickType')}</div>;
  }

  /**
   * Drag and drop messages.
   * Only available on create new question for now.
   * @param user
   * @param templateId
   * @param q
   * @param setQ
   * @param dispatch
   * @param mode
   * @returns
   */
  const dragAndDropTextHelper = (items: TSortableInput, containerIds: string[]) => {
    if (templateId && writesTemplateOverrides) {
      if (type === EQuestionType.Info) {
        const text = items['text'];
        if (!text?.items) {
          toast.error(t('createTextBubble.toast.textItemsNotFound'));
          return;
        }

        dispatch(
          customParametersUpdate({
            templateId,
            resource: {
              nodeId: questionId,
              key: 'text',
              value: text.items.map((item: any) => item.text),
              language: questionLanguage,
              isDefault: false,
            },
            eventType: EEventType.ADDED,
            description: `User updated ${questionId}/${'text'} messages.`,
          }),
        );
      } else {
        containerIds.map((containerId) => {
          // text not customizable
          if (containerId !== 'text') {
            const container = items[containerId];
            if (!container?.items) {
              toast.error(t('createTextBubble.toast.containerNotFound'));
              return;
            }

            dispatch(
              customParametersUpdate({
                templateId,
                resource: {
                  nodeId: questionId,
                  key: containerId as 'intro' | 'text' | 'outro' | 'clarify',
                  value: container.items.map((item: any) => item.text),
                  language: questionLanguage,
                  isDefault: false,
                },
                eventType: EEventType.ADDED,
                description: `User updated ${questionId}/${containerId} messages.`,
              }),
            );
          }
        });
      }
    }
    dispatch(dragAndDropText({ questionId, type, items }));
  };

  if (type === EQuestionType.Info) {
    return (
      <div className={clsx('p-1')}>
        <TextBubbleDragAndDrop
          disabled={isReadOnly}
          onShouldUpdate={(items, containers) => {
            dragAndDropTextHelper(items, containers);
          }}
          input={{
            text: {
              id: 'text',
              headerName: t('createTextBubble.headerNames.questionText'),
              items: text.value,
              isDefault: text.isDefault,
            },
          }}
          headerContent={(data) => <TextBubbleDragAndDropHeaderv2 data={data} />}
          itemContent={(data) => <TextBubblev2 data={data} />}
        />
      </div>
    );
  }

  if (type === EQuestionType.Open) {
    return (
      <div className={clsx('p-1')}>
        <TextBubbleDragAndDrop
          disabled={isReadOnly}
          onShouldUpdate={(items, containers) => {
            dragAndDropTextHelper(items, containers);
          }}
          input={{
            intro:
              intro && intro.value
                ? {
                    id: 'intro',
                    headerName: t('createTextBubble.headerNames.introText'),
                    items: intro.value,
                    isDefault: intro.isDefault,
                  }
                : undefined,
            text:
              text && text.value
                ? {
                    id: 'text',
                    headerName: t('createTextBubble.headerNames.text'),
                    items: text.value,
                    isDefault: text.isDefault,
                  }
                : undefined,
            outro:
              outro && outro.value
                ? {
                    id: 'outro',
                    headerName: t('createTextBubble.headerNames.outroText'),
                    items: outro.value,
                    isDefault: outro.isDefault,
                  }
                : undefined,
            clarify:
              clarify && clarify.value
                ? {
                    id: 'clarify',
                    headerName: t('createTextBubble.headerNames.clarifyText'),
                    items: clarify.value,
                    isDefault: clarify.isDefault,
                  }
                : undefined,
          }}
          headerContent={(data) => <TextBubbleDragAndDropHeaderv2 data={data} />}
          itemContent={(data) => <TextBubblev2 data={data} />}
        />
      </div>
    );
  }

  return (
    <div className={clsx('p-1')}>
      <TextBubbleDragAndDrop
        disabled={isReadOnly}
        onShouldUpdate={(items, containers) => {
          dragAndDropTextHelper(items, containers);
        }}
        input={{
          intro:
            intro && intro.value
              ? {
                  id: 'intro',
                  headerName: t('createTextBubble.headerNames.introText'),
                  items: intro.value,
                  isDefault: intro.isDefault,
                }
              : undefined,
          text:
            text && text.value
              ? {
                  id: 'text',
                  headerName: t('createTextBubble.headerNames.text'),
                  items: text.value,
                  isDefault: text.isDefault,
                }
              : undefined,
          outro:
            outro && outro.value
              ? {
                  id: 'outro',
                  headerName: t('createTextBubble.headerNames.outroText'),
                  items: outro.value,
                  isDefault: outro.isDefault,
                }
              : undefined,
        }}
        headerContent={(data) => <TextBubbleDragAndDropHeaderv2 data={data} />}
        itemContent={(data) => <TextBubblev2 data={data} />}
      />
    </div>
  );
};

export default CreateTextBubble;
